import { getCookiesClient } from '@/src/utils/cookie-parser';
import {
  ActionFieldPurchase,
  IDatalayerCustomEvent,
  IDatalayerPageDetails,
  IDatalayerUserInfo,
  PageDetails,
  PIPELINE_NAME,
  Purchase,
  PurchaseProduct,
  SERVER_CODE,
  Slider,
  VERSION,
} from './types';

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
};
declare const window: WindowWithDataLayer;

interface IEventData {
  toDataLayerObject(): any;

  push(): void;
}

function getUserIdentifiers() {
  const cookies = getCookiesClient();
  const sessionCookie = cookies?.['session.userInfo'];
  const fallbackTrackingId = cookies?.['userTrackingId'];

  let hkSha256Email = 'NULL';
  let hkUserTrackingId = '';
  let hkExternalUserId = null;
  let loginState = false;

  if (sessionCookie) {
    try {
      const parsedSession = JSON.parse(sessionCookie);
      hkSha256Email = parsedSession._hke || 'NULL';
      hkUserTrackingId = parsedSession.userTrackingId || '';
      hkExternalUserId = parsedSession._hkeui || null;
      loginState = true;
    } catch (error) {
      console.error('Error parsing session.userInfo cookie:', error);
    }
  } else if (fallbackTrackingId) {
    hkUserTrackingId = fallbackTrackingId;
  }

  return {
    hkSha256Email,
    hkUserTrackingId,
    hkExternalUserId,
    loginState,
  };
}


abstract class DataLayerEvent implements IEventData {
  abstract toDataLayerObject(): any;

  push() {
    window?.dataLayer?.push(this.toDataLayerObject());
  }
}

class OriginalLocationEvent extends DataLayerEvent {
  constructor(private readonly url: string) {
    super();
  }

  toDataLayerObject() {
    return {
      event: 'originalLocation',
      originalLocation: this.url,
    };
  }
}

class UserTrackingEvent extends DataLayerEvent {
  toDataLayerObject() {
    const {
      hkSha256Email,
      hkUserTrackingId,
      hkExternalUserId
    } = getUserIdentifiers();

    return {
      hkExternalUserId,
      hkUserTrackingId,
      hkSha256Email,
    };
  }
}

class PageDetailsEvent extends DataLayerEvent {
  constructor(private readonly pageDetails: PageDetails) {
    super();
  }

  toDataLayerObject() {
    const { pageData, clientData, type } = this.pageDetails;

    const virtualPageUrl =
      pageData.virtualPageUrl ||
      (typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '');

    const {
      hkSha256Email,
      hkUserTrackingId,
      loginState,
      hkExternalUserId,
    } = getUserIdentifiers();

    const rawData: Partial<IDatalayerPageDetails> = {
      event: 'GAVirtual',
      virtualPageUrl,
      virtualPageTitle: pageData.name,
      HK_pageCategory: String(pageData?.category),
      HK_pageMidCategory: String(pageData?.midCategory),
      HK_pageSubCategory: String(pageData?.subCategory),
      HK_pageType: String(pageData?.type),
      HK_pageName: String(pageData?.name),
      HK_serverCode: SERVER_CODE,
      HK_botStatus: clientData.HK_botStatus,
      version: VERSION,
      pipelineName: PIPELINE_NAME,
      loginState,
      hkExternalUserId,
      hkUserTrackingId,
      hkSha256Email,
    };

    if (type === 'cek' && pageData.step != null) {
      rawData.step = pageData.step;
    }

    // Filter out falsy values and 'NULL'
    const datalayerArgs = Object.fromEntries(
      Object.entries(rawData).filter(
        ([, value]) => value && value !== 'NULL'
      )
    ) as IDatalayerPageDetails;

    return datalayerArgs;
  }
}


class CustomRequestEvent extends DataLayerEvent {
  constructor(
    private readonly eventLabel: string,
    private readonly eventAction: string = 'Kobi',
    private readonly eventCategory: string,
    private readonly eventValue?: string
  ) {
    super();
  }

  toDataLayerObject(): IDatalayerCustomEvent {
    return {
      event: 'GAEvent',
      eventCategory: this.eventCategory,
      eventAction: this.eventAction,
      eventLabel: this.eventLabel,
      // eventValue: this.eventValue,
    };
  }
}

class CustomRequestEventLogin extends DataLayerEvent {
  constructor(
    private readonly eventLabel: string,
    private readonly eventAction: string = 'Kobi',
    private readonly eventCategory: string,
    private readonly memberId: string
  ) {
    super();
  }

  toDataLayerObject(): IDatalayerCustomEvent {
    return {
      event: 'login',
      eventCategory: this.eventCategory,
      eventAction: this.eventAction,
      eventLabel: this.eventLabel,
      memberId: this.memberId,
      // eventValue: this.eventValue,
    };
  }
}

class CustomRequestEventSignup extends DataLayerEvent {
  constructor(
    private readonly eventLabel: string,
    private readonly eventAction: string = 'Kobi',
    private readonly eventCategory: string,
    private readonly memberId: string
  ) {
    super();
  }

  toDataLayerObject(): IDatalayerCustomEvent {
    return {
      event: 'sign_up',
      eventCategory: this.eventCategory,
      eventAction: this.eventAction,
      eventLabel: this.eventLabel,
      memberId: this.memberId,
      // eventValue: this.eventValue,
    };
  }
}

class CustomSliderClickEvent extends DataLayerEvent {
  constructor(
    private readonly eventLabel: string,
    private readonly eventAction: string = 'Kobi',
    private readonly eventCategory: string,
    private readonly id: string,
    private readonly name: string,
    private readonly creative: string,
    private readonly position: number | string
  ) {
    super();
  }

  toDataLayerObject(): Slider {
    return {
      event: 'ecInternalPromotionClick',
      eventCategory: this.eventCategory,
      eventAction: this.eventAction,
      eventLabel: this.eventLabel,
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id: this.id,
              name: this.name,
              creative: this.creative,
              position: this.position,
            },
          ],
        },
      },
    };
  }
}

class CustomSliderViewEvent extends DataLayerEvent {
  constructor(
    private readonly eventLabel: string,
    private readonly eventAction: string = 'Kobi',
    private readonly eventCategory: string,
    private readonly id: string,
    private readonly name: string,
    private readonly creative: string,
    private readonly position: number | string
  ) {
    super();
  }

  toDataLayerObject(): Slider {
    return {
      event: 'ecInternalPromotionImpression',
      eventCategory: this.eventCategory,
      eventAction: this.eventAction,
      eventLabel: this.eventLabel,
      ecommerce: {
        promoView: {
          promotions: [
            {
              id: this.id,
              name: this.name,
              creative: this.creative,
              position: this.position,
            },
          ],
        },
      },
    };
  }
}

class PurchaseEvent extends DataLayerEvent {
  constructor(
    private readonly eventLabel: string,
    private readonly actionFields: ActionFieldPurchase,
    private readonly products: PurchaseProduct[]
  ) {
    super();
  }

  toDataLayerObject(): Purchase {
    return {
      event: 'purchase',
      eventCategory: 'Enhanced Ecommerce',
      eventAction: 'Purchase',
      eventLabel: this.eventLabel,
      ecommerce: {
        purchase: {
          actionField: {
            id: this.actionFields.id,
            revenue: this.actionFields.revenueNumber ?? String(this.actionFields.revenue ?? 0),
            action: 'purchase',
            currency: 'TRY', // static
            ...(this.actionFields.list ? { list: this.actionFields.list } : {})
          },
          products: this.products,
        },
      },
    };
  }
}

class LoginStateEvent extends DataLayerEvent {
  toDataLayerObject(): IDatalayerUserInfo {
    return {
      loginState: false,
    };
  }
}

interface IDataLayerService {
  sendEvent(eventData: IEventData): void;
}

class DataLayerService implements IDataLayerService {
  sendEvent(eventData: IEventData) {
    eventData.push();
  }
}

export {
  DataLayerService,
  OriginalLocationEvent,
  PageDetailsEvent,
  CustomRequestEvent,
  CustomRequestEventLogin,
  CustomRequestEventSignup,
  PurchaseEvent,
  LoginStateEvent,
  CustomSliderClickEvent,
  CustomSliderViewEvent,
  UserTrackingEvent
};
